import React from 'react';
import {
    IonList,
    IonItem,
    IonLabel,
    IonButton,
    IonIcon,
    IonFooter,
    IonToolbar,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
} from '@ionic/react';
import { trashOutline } from 'ionicons/icons';
import { CartItem } from '../../../models/shop.model';
import { formatMoney } from '../../../utils/currency';
import CurrencyPrefix from '../../../components/financials/currency-prefix';
import { QuantityStepper } from '../../../components/common/QuantityStepper/QuantityStepper';

export interface StorefrontCartProps {
    cart: CartItem[];
    cartTotal: number;
    currencyPrefix?: string;
    onUpdateQuantity: (id: string, quantity: number) => void;
    onRemoveItem: (id: string) => void;
    onClearCart: () => void;
    onProceed: () => void;
}

export const StorefrontCartComponent: React.FC<StorefrontCartProps> = ({
    cart,
    cartTotal,
    currencyPrefix,
    onUpdateQuantity,
    onRemoveItem,
    onClearCart,
    onProceed,
}) => {
    if (cart.length === 0) {
        return (
            <div data-testid="storefront-cart-empty" className="ion-text-center ion-padding">
                <p>Your basket is empty.</p>
                <IonButton routerLink="/shop" expand="block" fill="clear">
                    Back to Shop
                </IonButton>
            </div>
        );
    }

    return (
        <>
            <IonList data-testid="storefront-cart-items">
                {cart.map((item) => (
                    <IonItemSliding key={item.id}>
                        <IonItem>
                            <IonLabel>
                                <h2>{item.product.name}</h2>
                                <p>
                                    <CurrencyPrefix prefix={currencyPrefix} />
                                    {item.product.priceDisplay} each
                                </p>
                            </IonLabel>
                            <div slot="end">
                                <QuantityStepper
                                    quantity={item.quantity}
                                    onDecrement={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                    onIncrement={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                    data-testid={`stepper-${item.id}`}
                                />
                            </div>
                        </IonItem>
                        <IonItemOptions side="end">
                            <IonItemOption
                                color="danger"
                                data-testid={`remove-item-${item.id}`}
                                onClick={() => onRemoveItem(item.id)}
                            >
                                <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonItemOption>
                        </IonItemOptions>
                    </IonItemSliding>
                ))}

                <IonItem lines="none" className="ion-margin-top">
                    <IonButton routerLink="/shop" expand="block" fill="clear">
                        Back to Shop
                    </IonButton>
                    <IonButton color="danger" fill="clear" onClick={onClearCart} slot="end">
                        Clear Basket
                    </IonButton>
                </IonItem>
            </IonList>

            <IonFooter>
                <IonToolbar color="primary">
                    <div slot="start" style={{ paddingLeft: '16px', fontWeight: 'bold' }}>
                        Total:{' '}
                        <CurrencyPrefix prefix={currencyPrefix} />
                        <span data-testid="storefront-cart-total">{formatMoney(cartTotal)}</span>
                    </div>
                    <IonButton
                        slot="end"
                        fill="clear"
                        color="light"
                        data-testid="proceed-to-checkout"
                        onClick={onProceed}
                        disabled={cart.length === 0}
                    >
                        <span style={{ color: 'white' }}>Proceed to checkout</span>
                    </IonButton>
                </IonToolbar>
            </IonFooter>
        </>
    );
};

export default StorefrontCartComponent;
